import { useEffect, useMemo, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { useHistory } from "react-router-dom";

import moment from "moment";

import { Calendar } from "@smartrent/icons";
import { Button, Typography, VStack } from "@smartrent/ui";

import { AddToCalendarModal } from "@/components/AddToCalendarModal";
import { getCaption } from "@/components/Intro";
import FullScreenLoader from "@/components/common/FullScreenLoader";
import { Modal } from "@/components/common/Modal";
import { NearbyCommunityCard } from "@/components/common/NearbyCommunityCard";
import AppShell from "@/containers/AppShell";
import { useApi } from "@/hooks/api";
import { useInterval } from "@/hooks/interval";
import {
  ApiNearbyCommunityType,
  ConfigType,
  NearbyCommunityType,
  TourType,
} from "@/types";
import { normalizeNearbyCommunity } from "@/utils/community";
import { isV2FrontendEnabled } from "@/utils/v2";

export function NearbyCommunities({
  config,
  tour,
}: {
  config: ConfigType;
  tour: TourType;
}) {
  const isV2Frontend = isV2FrontendEnabled();
  const history = useHistory();

  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const { response, error } = useApi({
    url: `/group/${config.groupId}/nearby-properties`,
    trigger: config,
  });

  const nearbyCommunities: NearbyCommunityType[] | null = useMemo(() => {
    return (
      response?.data?.enabled_nearby_properties?.map(
        (community: ApiNearbyCommunityType) =>
          normalizeNearbyCommunity(community)
      ) ?? null
    );
  }, [response?.data?.enabled_nearby_properties?.map]);

  useInterval(function () {
    if (moment(tour.scheduledStart) < moment(new Date()) && !isV2Frontend) {
      history.push(`/tour/${tour.token}`);
    } else {
      history.push(`/tour/${tour.token}/checklist`);
    }
  }, 5000);

  useEffect(() => {
    if ((error || tour.status === "active") && !isV2Frontend) {
      history.replace(`/tour/${tour.token}`);
    } else {
      history.push(`/tour/${tour.token}/checklist`);
    }
  }, [error, history, tour.status, tour.token]);

  if (!response) {
    return <FullScreenLoader />;
  }

  return (
    <>
      <ScrollView style={styles.root}>
        <AppShell header={{ left: null }} showNewTourButton={false}>
          <VStack spacing={12} style={{ padding: 16 }}>
            <Typography type="title">Tour Scheduled</Typography>
            <Typography type="body" color="primary">
              {`Your tour at ${config.propertyName} is ${getCaption({
                tourStatus: tour.status,
                tourStart: tour.scheduledStart || "",
                tourEnd: tour.scheduledEnd || "",
                disableDoubleBookings: config.disableDoubleBookings,
                startOfSentence: false,
              })}`}
            </Typography>

            <View style={{ marginTop: 18 }}>
              <View style={{ marginBottom: 12 }}>
                <Button
                  size="large"
                  iconLeft={Calendar}
                  onPress={() => setModalVisible(true)}
                >
                  Add to Calendar
                </Button>
              </View>

              <View>
                <Button
                  size="large"
                  variation="outlined"
                  onPress={() => {
                    history.push(`/tour/${tour.token}`);
                  }}
                >
                  View Tour Details
                </Button>
              </View>
            </View>

            <View>
              <VStack spacing={16} style={{ marginTop: 32, marginBottom: 16 }}>
                <Typography type="title3">Nearby Communities</Typography>
                <Typography type="body">
                  We found some communities near you that you may be interested
                  in.
                </Typography>
              </VStack>
              <View>
                {nearbyCommunities
                  ? nearbyCommunities.map(
                      (community: NearbyCommunityType, index: number) => {
                        return (
                          <NearbyCommunityCard
                            key={index}
                            community={community}
                            onPress={() => {
                              window.location.href = `/property/${community.tourConfigUuid}/units`;
                            }}
                          />
                        );
                      }
                    )
                  : null}
              </View>
            </View>
          </VStack>
        </AppShell>
      </ScrollView>

      <Modal visible={modalVisible} faded>
        <AddToCalendarModal
          tour={tour}
          config={config}
          onClose={() => setModalVisible(false)}
        />
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  root: {
    width: "100%",
    height: "100%",
    position: "relative",
  },
});
